import { CheckIcon, PaletteIcon } from 'lucide-react';
import type { DocumentThemePresetDTO } from '@shared/schemas/document-model';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { THEME_OPTIONS } from './canvas-config';

type CanvasThemeMenuProps = {
  currentPreset: DocumentThemePresetDTO | null;
  disabled: boolean;
  onChange: (preset: DocumentThemePresetDTO) => void;
};

/** Pastille bicolore : primaire / accent du preset. */
function ThemeSwatch({ accent, primary }: { accent: string; primary: string }) {
  return (
    <span
      aria-hidden
      className="size-5 shrink-0 rounded-full ring-1 ring-foreground/10"
      style={{ background: `linear-gradient(135deg, ${primary} 0 58%, ${accent} 58% 100%)` }}
    />
  );
}

export function CanvasThemeMenu({ currentPreset, disabled, onChange }: CanvasThemeMenuProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          aria-label="Changer le thème du document"
          className="h-8 gap-1.5 px-2.5 @lg:px-3"
          disabled={disabled}
          size="sm"
          type="button"
          variant="ghost"
        >
          <PaletteIcon className="size-3.5" aria-hidden />
          <span className="hidden @2xl:inline">Thème</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2">
        <p className="px-2 pb-1.5 pt-1 text-xs font-medium text-muted-foreground">Thème du document</p>
        <div aria-label="Thèmes" className="flex flex-col gap-0.5" role="radiogroup">
          {THEME_OPTIONS.map((option) => {
            const isCurrent = option.preset === currentPreset;
            return (
              <button
                aria-checked={isCurrent}
                className={cn(
                  'flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-muted focus-visible:bg-muted',
                  isCurrent && 'bg-muted/70 font-medium',
                )}
                key={option.preset}
                onClick={() => {
                  if (!isCurrent) {
                    onChange(option.preset);
                  }
                }}
                role="radio"
                type="button"
              >
                <ThemeSwatch accent={option.accent} primary={option.primary} />
                <span className="flex-1">{option.label}</span>
                {isCurrent ? <CheckIcon className="size-3.5 text-muted-foreground" aria-hidden /> : null}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
